import React, { useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Select from "ol/interaction/Select.js";

import {
    Box,
    Card,
    CardContent,
    CardHeader,
    ClickAwayListener,
    Collapse,
    Divider,
    IconButton,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Tooltip,
    Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { singleClick } from "ol/events/condition";
import { useCuas } from "../contexts/cuasContext";
import { useUserAuth } from "../contexts/authContext";
import { CustomPagination } from "../customPagination";
import { ConvertISOToDate, ConvertMetersToFeet } from "../util";
import { getCoordinateLocationFromSettings, IsMapBeingDrawnOn, JumpToFeature } from "../map/mapUtil";

export default function AssetInfo({ map }) {
    const [popperPosition, setPopperPosition] = useState(null);
    const [featuresClicked, setFeaturesClicked] = useState([]);
    const [featureInfo, setFeatureInfo] = useState([]);
    const [featureDeleted, setFeatureDeleted] = useState(false);
    const [expandedCard, setExpandedCard] = useState(false);

    const selectRef = useRef(null);
    const featuresClickedRef = useRef([]);
    const { assetMonitors, getAssetMonitor, startAssetMonitoring, stopAssetMonitoring } = useCuas();
    const { userMapSettings } = useUserAuth();

    const infoMapping = {
        asd: (asd) => {
            const info = [
                { title: "Callsign", value: asd.call_sign },
                { title: "Track Angle", value: `${asd.course_deg}°` },
                { title: "Position", value: getCoordinateLocationFromSettings([asd.lon_deg, asd.lat_deg], userMapSettings, 5) },
                { title: "Altitude (HAE)", value: `${asd.alt_hae_ft}ft` },
                { title: "Ground Speed", value: `${asd.ground_speed_kn}kn` },
                { title: "Source Type", value: asd.source_type },
                { title: "Classification", value: asd.classification },
                { title: "Source Name", value: asd.source_name || asd.source_id },
                { title: "Squawk Code", value: asd.squawk || "None" },
                { title: "Last Update", value: ConvertISOToDate(asd.timestamp) }
            ];
            return info;
        },
        asset: (asset) => {
            const monitor = getAssetMonitor(featuresClicked[0].getId());
            if (monitor) {
                return [
                    { title: "Type", value: "Asset" },
                    { title: "Buffer", value: `${ConvertMetersToFeet(asset.buffer_m)}ft` },
                    { title: "Altitude (AGL)", value: `${ConvertMetersToFeet(asset.buffer_altitude_agl_m)}ft` },
                    { title: "Altitude (HAE)", value: `${ConvertMetersToFeet(asset.buffer_altitude_hae_m)}ft` },
                    { title: "High Alerts", value: monitor.get("highAlerts"), style: { color: "red", fontWeight: "bolder" } },
                    { title: "Medium Alerts", value: monitor.get("mediumAlerts"), style: { color: "yellow", fontWeight: "lighter" } },
                    { title: "No Risk Alerts", value: monitor.get("noRiskAlerts"), style: { color: "lime" } }
                ];
            } else {
                return [
                    { title: "Type", value: "Asset" },
                    { title: "Buffer", value: `${ConvertMetersToFeet(asset.buffer_m)}ft` },
                    { title: "Altitude (AGL)", value: `${ConvertMetersToFeet(asset.buffer_altitude_agl_m)}ft` },
                    { title: "Altitude (HAE)", value: `${ConvertMetersToFeet(asset.buffer_altitude_hae_m)}ft` }
                ];
            }
        }
    };

    useEffect(() => {
        if (!map) {
            return;
        }

        setupMapSelection();
        setupDynamicCursor();
        handleFeatureChanges();

        return () => {
            map.removeInteraction(selectRef.current);
        };
    }, [map]);

    useEffect(() => {
        featuresClickedRef.current = featuresClicked;

        // Make sure the Select interaction is up-to-date.
        if (selectRef.current?.getFeatures() && selectRef.current.getFeatures().getLength() !== featuresClicked.length) {
            selectRef.current.getFeatures().clear();
            selectRef.current.getFeatures().extend(featuresClicked);
        }

        // We will only get the info of a feature if there is only 1 selected.
        if (featuresClicked.length !== 1) {
            setFeatureDeleted(false);
            return;
        }

        // Get the entity's info to display in the info card.
        const properties = featuresClicked[0].getProperties();
        for (const type in infoMapping) {
            if (type in properties) {
                const featureInfo = infoMapping[type](properties[type]);
                setFeatureInfo(featureInfo);
                break;
            }
        }
    }, [featuresClicked, assetMonitors]);

    // Sets up all of the map selection logic (clicking of features on the map).
    const setupMapSelection = () => {
        const handleMapSelection = (event) => {
            const pointEvent = event.mapBrowserEvent.originalEvent;
            const virtualElement = {
                getBoundingClientRect: () => ({
                    width: 0,
                    height: 0,
                    top: pointEvent.y,
                    right: pointEvent.x,
                    bottom: pointEvent.y,
                    left: pointEvent.x
                }),
                nodeType: 1
            };
            setPopperPosition(virtualElement);
            setFeaturesClicked(event.selected);
            setFeatureDeleted(false);
        };

        // Drill pick to open up the info card of features.
        const select = new Select({
            // Only check layers that were designated as selectable.
            layers: (layer) => layer.get("selectable"),
            // We don't want to be able to click on features if the map is being drawn on.
            condition: (event) => {
                if (IsMapBeingDrawnOn(map)) {
                    return false;
                } else {
                    return singleClick(event);
                }
            },
            // Ignore history and prediction lines.
            filter: (feature) => feature.getId() && !feature.getId().endsWith("_pl") && !feature.getId().endsWith("_hl"),
            multi: true,
            style: null,
            hitTolerance: 3
        });

        selectRef.current = select;
        select.on("select", (event) => handleMapSelection(event));
        map.addInteraction(select);
    };

    // Sets up the cursor changing to pointer when hovering over selectable layers.
    const setupDynamicCursor = () => {
        map.on("pointermove", (event) => {
            if (IsMapBeingDrawnOn(map)) {
                return;
            }

            let selectableLayer = false;
            map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
                if (layer.get("selectable") && feature.getId() && !feature.getId().endsWith("_pl") && !feature.getId().endsWith("_hl")) {
                    selectableLayer = true;
                    return true;
                }
            });
            map.getTargetElement().style.cursor = selectableLayer ? "pointer" : "";
        });
    };

    // Handles the changes and removals of features whose info is being displayed.
    const handleFeatureChanges = () => {
        map.getLayers().forEach((layer) => {
            if (!layer.get("selectable")) {
                return;
            }
            const source = layer.getSource();
            source.on("changefeature", (event) => {
                const changedFeature = event.feature;
                if (featuresClickedRef.current.length === 1 && featuresClickedRef.current[0].getId() === changedFeature.getId()) {
                    setFeatureDeleted(false);
                    setFeaturesClicked([changedFeature]);
                }
            });
            source.on("removefeature", (event) => {
                const removedFeature = event.feature;
                if (featuresClickedRef.current.length === 1 && featuresClickedRef.current[0].getId() === removedFeature.getId()) {
                    if (removedFeature.get("asd")) {
                        setFeatureDeleted(true);
                    } else {
                        setFeaturesClicked([]);
                    }
                } else if (featuresClickedRef.current.length > 1) {
                    setFeaturesClicked((prev) => prev.filter((feature) => feature.getId() !== removedFeature.getId()));
                }
            });
        });
    };

    const handleMonitorButtonClick = (assetMonitor) => {
        if (assetMonitor) {
            stopAssetMonitoring(map, featuresClicked[0].getId());
        } else {
            startAssetMonitoring(map, featuresClicked[0].getId());
        }
    };

    const columns = [
        {
            field: "name",
            headerName: "Name",
            width: 170,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {params.row.get("name")}
                </Typography>
            )
        },
        {
            field: "altitude",
            headerName: "Altitude",
            width: 73,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {`${params.row.get("asd").alt_hae_ft}ft`}
                </Typography>
            )
        },
        {
            field: "speed",
            headerName: "Speed",
            width: 63,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {`${params.row.get("asd").ground_speed_kn}kn`}
                </Typography>
            )
        }
    ];

    const assetMonitor = featuresClicked.length === 1 ? getAssetMonitor(featuresClicked[0].getId()) : null;
    return featuresClicked.length > 1 ? (
        // Shows the feature menu list if multiple features are selected at once.
        <Popper open={!!popperPosition} anchorEl={popperPosition} placement={"bottom-end"}>
            <Paper>
                <ClickAwayListener onClickAway={() => setFeaturesClicked([])}>
                    <MenuList dense sx={{ maxHeight: 200, overflow: "auto" }}>
                        {featuresClicked.map((feature, i) => (
                            <MenuItem key={i} onClick={() => setFeaturesClicked([feature])}>
                                {feature.get("name")}
                            </MenuItem>
                        ))}
                    </MenuList>
                </ClickAwayListener>
            </Paper>
        </Popper>
    ) : featuresClicked.length === 1 ? (
        // Shows the info card if only 1 feature is selected.
        <Card
            raised
            sx={{
                position: "absolute",
                zIndex: "1000",
                top: "45px",
                right: "10px",
                maxHeight: "600px",
                minWidth: "300px",
                maxWidth: "340px",
                boxShadow: featureDeleted ? "0 0 4px 2.5px rgba(255, 0, 0, 0.5)" : "0 4px 10px rgba(0, 0, 0, 0.2)"
            }}
        >
            <CardHeader
                disableTypography
                title={
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            width: "100%",
                            overflow: "hidden"
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                flexGrow: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontStyle: featureDeleted ? "italic" : "normal",
                                mr: "auto"
                            }}
                        >
                            {featuresClicked[0].get("name")}
                        </Typography>
                        {[
                            {
                                title: "Monitor",
                                icon: assetMonitor ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />,
                                visible: "asset" in featuresClicked[0].getProperties() && !featureDeleted,
                                onClick: () => handleMonitorButtonClick(assetMonitor)
                            },
                            {
                                title: "Close",
                                icon: <CloseIcon fontSize="small" />,
                                visible: true,
                                onClick: () => setFeaturesClicked([])
                            }
                        ].map(({ title, icon, visible, onClick }, i) =>
                            visible ? (
                                <Tooltip key={i} title={title}>
                                    <IconButton size="small" aria-label="Monitor" onClick={onClick}>
                                        {icon}
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <React.Fragment key={i} />
                            )
                        )}
                    </Box>
                }
                sx={{
                    p: "4px 4px 4px 12px !important",
                    width: "100%",
                    "& .MuiCardHeader-content": {
                        width: "100%"
                    }
                }}
            />
            <Divider />
            <CardContent sx={{ display: "grid", gridTemplateColumns: "1fr", gap: "4px", pt: "12px !important", pb: "14px !important" }}>
                {featureInfo.map((info, i) => (
                    <Typography key={i} variant="body2" color="text.secondary" sx={{ display: "flex", justifyContent: "space-between" }}>
                        <b>{info.title}:&nbsp;</b>
                        <span style={info.style}>{info.value}</span>
                    </Typography>
                ))}
            </CardContent>

            {/* Additional info for assets currently being monitored. */}
            {assetMonitor ? (
                <>
                    {expandedCard ? <Divider /> : <></>}
                    <CardContent sx={{ display: "flex", flexDirection: "column", pt: `${expandedCard ? "8px" : "0px"} !important`, pb: "14px !important" }}>
                        <Collapse in={expandedCard} timeout="auto" unmountOnExit>
                            <DataGrid
                                rows={assetMonitor.get("cuas")}
                                getRowId={(row) => row.getId()}
                                columns={columns}
                                disableRowSelectionOnClick
                                disableVirtualization
                                autoHeight
                                columnHeaderHeight={32}
                                rowHeight={32}
                                pagination
                                slots={{ pagination: CustomPagination }}
                                initialState={{ pagination: { paginationModel: { pageSize: 6 } } }}
                                onRowClick={(params) => JumpToFeature(map, params.row)}
                                sx={{
                                    border: "none",
                                    "& .MuiDataGrid-columnHeaderTitle": { color: "text.secondary", fontWeight: "bold" },
                                    "& .MuiDataGrid-columnHeaders": { borderBottom: "none", backgroundColor: "inherit !important" },
                                    "& .MuiDataGrid-footerContainer": { borderTop: "none" }
                                }}
                            />
                        </Collapse>
                        <IconButton onClick={() => setExpandedCard(!expandedCard)} sx={{ alignSelf: "center" }}>
                            {expandedCard ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </CardContent>
                </>
            ) : (
                <></>
            )}
        </Card>
    ) : (
        <></>
    );
}
